import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, CardBody, Spinner } from 'reactstrap';
import { Link } from 'react-router-dom';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import BreadCrumb from '../components/Common/BreadCrumb';
import { dashboardService, contentService } from '../services';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const DashboardPage: React.FC = () => {
  const [stats, setStats] = useState<{
    totalContent: number;
    totalUsers: number;
    totalCategories: number;
    parentCategoriesCount: number;
    contentByStatus: { published: number; draft: number; archived: number };
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cleanupState, setCleanupState] = useState<{
    running: boolean;
    result: { deleted: number; freedBytes: number } | null;
    error: string | null;
  }>({ running: false, result: null, error: null });

  const runCleanup = async () => {
    setCleanupState({ running: true, result: null, error: null });
    try {
      const result = await contentService.cleanupUploads();
      setCleanupState({ running: false, result, error: null });
    } catch (err: any) {
      setCleanupState({ running: false, result: null, error: err?.response?.data?.message || 'Cleanup failed' });
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await dashboardService.getStats();
        setStats(data);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Failed to load dashboard stats');
        setStats(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Content Status (from API)
  const statusData = stats
    ? {
        labels: ['Published', 'Draft', 'Archived'],
        datasets: [
          {
            data: [
              stats.contentByStatus.published,
              stats.contentByStatus.draft,
              stats.contentByStatus.archived,
            ],
            backgroundColor: [
              'rgba(10, 179, 156, 0.8)',
              'rgba(255, 193, 7, 0.8)',
              'rgba(108, 117, 125, 0.8)',
            ],
            borderColor: [
              'rgb(10, 179, 156)',
              'rgb(255, 193, 7)',
              'rgb(108, 117, 125)',
            ],
            borderWidth: 2,
          },
        ],
      }
    : {
        labels: ['Published', 'Draft', 'Archived'],
        datasets: [{ data: [0, 0, 0], backgroundColor: ['#0ab39c', '#ffc107', '#6c757d'], borderWidth: 2 }],
      };

  // Placeholder for Content Growth (no time-series API - show empty or static)
  const contentGrowthData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Content',
        data: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, stats?.totalContent ?? 0],
        borderColor: 'rgb(64, 81, 137)',
        backgroundColor: 'rgba(64, 81, 137, 0.2)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Placeholder for Content by Category (no per-category count API)
  const categoryDistributionData = {
    labels: ['Categories'],
    datasets: [
      {
        label: 'Total Categories',
        data: [stats?.totalCategories ?? 0],
        backgroundColor: 'rgba(64, 81, 137, 0.8)',
        borderColor: 'rgb(64, 81, 137)',
        borderWidth: 1,
      },
    ],
  };

  // User count bar (from API)
  const engagementData = {
    labels: ['Users'],
    datasets: [
      {
        label: 'Total Users',
        data: [stats?.totalUsers ?? 0],
        backgroundColor: 'rgba(94, 163, 203, 0.8)',
        borderColor: 'rgb(94, 163, 203)',
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
      },
    },
  };

  if (loading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Analytics" pageTitle="Dashboard" />
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Loading dashboard...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="Analytics" pageTitle="Dashboard" />
          {error && (
            <div className="alert alert-warning mb-3" role="alert">
              <i className="ri-error-warning-line me-2"></i>
              {error}
            </div>
          )}

          {/* Stats Cards - real data from backend */}
          <Row>
            <Col xl={3} md={6}>
              <Card className="card-animate">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-uppercase fw-medium text-muted mb-0">Total Content</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-end justify-content-between mt-4">
                    <div>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                        <span className="counter-value">{stats?.totalContent ?? 0}</span>
                      </h4>
                      <Link to="/dashboard/content" className="badge bg-primary-subtle text-primary text-decoration-none mb-0">
                        <i className="ri-file-list-line align-middle"></i> View all
                      </Link>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-primary-subtle rounded fs-3">
                        <i className="ri-file-text-line text-primary"></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={3} md={6}>
              <Card className="card-animate">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-uppercase fw-medium text-muted mb-0">Total Users</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-end justify-content-between mt-4">
                    <div>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                        <span className="counter-value">{stats?.totalUsers ?? 0}</span>
                      </h4>
                      <Link to="/dashboard/users" className="badge bg-info-subtle text-info text-decoration-none mb-0">
                        <i className="ri-group-line align-middle"></i> View all
                      </Link>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-info-subtle rounded fs-3">
                        <i className="ri-group-line text-info"></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={3} md={6}>
              <Card className="card-animate">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-uppercase fw-medium text-muted mb-0">Categories</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-end justify-content-between mt-4">
                    <div>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                        <span className="counter-value">{stats?.totalCategories ?? 0}</span>
                      </h4>
                      <span className="badge bg-success-subtle text-success mb-0">
                        <i className="ri-folder-line align-middle"></i> {stats?.parentCategoriesCount ?? 0} Parents
                      </span>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-success-subtle rounded fs-3">
                        <i className="ri-folder-open-line text-success"></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            <Col xl={3} md={6}>
              <Card className="card-animate">
                <CardBody>
                  <div className="d-flex align-items-center">
                    <div className="flex-grow-1">
                      <p className="text-uppercase fw-medium text-muted mb-0">Content Status</p>
                    </div>
                  </div>
                  <div className="d-flex align-items-end justify-content-between mt-4">
                    <div>
                      <h4 className="fs-22 fw-semibold ff-secondary mb-4">
                        <span className="counter-value">{stats?.contentByStatus?.published ?? 0}</span>
                      </h4>
                      <span className="badge bg-warning-subtle text-warning mb-0">
                        <i className="ri-checkbox-circle-line align-middle"></i> Published
                      </span>
                    </div>
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-warning-subtle rounded fs-3">
                        <i className="ri-time-line text-warning"></i>
                      </span>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Charts Row - real data from backend */}
          <Row>
            <Col xl={8}>
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center mb-4">
                    <h5 className="card-title mb-0">Content Overview</h5>
                  </div>
                  <div style={{ height: '350px' }}>
                    <Line data={contentGrowthData} options={chartOptions} />
                  </div>
                  <p className="text-muted small mb-0 mt-2">Total content: {stats?.totalContent ?? 0}</p>
                </CardBody>
              </Card>
            </Col>

            <Col xl={4}>
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center mb-4">
                    <h5 className="card-title mb-0">Content by Status</h5>
                  </div>
                  <div style={{ height: '350px' }}>
                    <Doughnut data={statusData} options={doughnutOptions} />
                  </div>
                  <p className="text-muted small mb-0 mt-2">Published / Draft / Archived from API</p>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Storage Cleanup */}
          <Row className="mb-2">
            <Col xl={4} md={6}>
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center mb-3">
                    <div className="avatar-sm flex-shrink-0 me-3">
                      <span className="avatar-title bg-danger-subtle rounded fs-3">
                        <i className="ri-hard-drive-line text-danger"></i>
                      </span>
                    </div>
                    <div>
                      <h5 className="card-title mb-0">Storage Cleanup</h5>
                      <p className="text-muted small mb-0">Remove orphaned upload files</p>
                    </div>
                  </div>

                  {cleanupState.result && (
                    <div className="alert alert-success py-2 mb-3">
                      <i className="ri-checkbox-circle-line me-1"></i>
                      Deleted <strong>{cleanupState.result.deleted}</strong> file(s) &mdash; freed{' '}
                      <strong>{(cleanupState.result.freedBytes / 1024 / 1024).toFixed(1)} MB</strong>
                    </div>
                  )}
                  {cleanupState.error && (
                    <div className="alert alert-danger py-2 mb-3">
                      <i className="ri-error-warning-line me-1"></i>
                      {cleanupState.error}
                    </div>
                  )}

                  <button
                    className="btn btn-danger btn-sm w-100"
                    onClick={runCleanup}
                    disabled={cleanupState.running}
                  >
                    {cleanupState.running ? (
                      <><Spinner size="sm" className="me-2" />Cleaning up...</>
                    ) : (
                      <><i className="ri-delete-bin-line me-1"></i>Clean Orphaned Files</>
                    )}
                  </button>
                  <p className="text-muted small mt-2 mb-0">
                    Deletes temp video/audio files older than 1 hour that were never uploaded to S3.
                  </p>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Row>
            <Col xl={6}>
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center mb-4">
                    <h5 className="card-title mb-0">Categories</h5>
                  </div>
                  <div style={{ height: '300px' }}>
                    <Bar data={categoryDistributionData} options={chartOptions} />
                  </div>
                  <p className="text-muted small mb-0 mt-2">Total categories: {stats?.totalCategories ?? 0} ({stats?.parentCategoriesCount ?? 0} parent)</p>
                </CardBody>
              </Card>
            </Col>

            <Col xl={6}>
              <Card>
                <CardBody>
                  <div className="d-flex align-items-center mb-4">
                    <h5 className="card-title mb-0">Total Users</h5>
                  </div>
                  <div style={{ height: '300px' }}>
                    <Bar data={engagementData} options={chartOptions} />
                  </div>
                  <p className="text-muted small mb-0 mt-2">Registered users: {stats?.totalUsers ?? 0}</p>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DashboardPage;
